// Tiny presentational chip that shows where an action came from.
// Surfaces alongside priority/status pills on rows and detail header.

import { cn } from "@/lib/cn";

interface ActionSourceChipProps {
  source: string | null | undefined;
  className?: string;
}

// Tones tuned to be distinct from PRIORITY_TONES (urgent/critical use red/orange/amber)
// and STATUS_TONES (open=blue, in_progress=indigo). Sources sit in their own palette.
const SOURCE_TONES: Record<string, string> = {
  email_triage:   "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
  meeting:        "bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-200",
  whatsapp:       "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  manual:         "bg-muted text-muted-foreground",
  tminus:         "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200",
  planning_sheet: "bg-teal-100 text-teal-900 dark:bg-teal-950 dark:text-teal-200",
  other:          "bg-muted text-muted-foreground",
};

const SOURCE_LABELS: Record<string, string> = {
  email_triage:   "📧 Email triage",
  meeting:        "🎙️ Meeting",
  whatsapp:       "💬 WhatsApp",
  manual:         "👤 Manual",
  tminus:         "⏱ T-Minus",
  planning_sheet: "📋 Planning",
  other:          "Other",
};

export function ActionSourceChip({ source, className }: ActionSourceChipProps) {
  const key = (source ?? "other").toLowerCase();
  const tone = SOURCE_TONES[key] ?? SOURCE_TONES.other;
  const label = SOURCE_LABELS[key] ?? key;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
        tone,
        className,
      )}
      title={`Source: ${key}`}
    >
      {label}
    </span>
  );
}
